import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Manage current Basic Transaction tab via URL search params.
 * - Param: `transaction-type` in ['expense','income','loan','transfer']
 * - Defaults to 'expense' when missing/invalid
 *
 * Returns a tuple: [currentTab, setCurrentTab]
 */
export type TransactionFormTab = 'expense' | 'income' | 'loan' | 'transfer' | 'credit_card';

const PARAM_KEY = 'transaction-type';
const DEFAULT_TAB: TransactionFormTab = 'expense';

const useTransactionFormType = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const lastValidRef = useRef<TransactionFormTab>(DEFAULT_TAB);

  const currentTransactionType = useMemo(() => {
    const raw = searchParams.get(PARAM_KEY) as TransactionFormTab | null;
    const validTabs: readonly TransactionFormTab[] = ['expense', 'income', 'loan', 'transfer', 'credit_card'] as const;
    if (raw && (validTabs as readonly string[]).includes(raw)) {
      lastValidRef.current = raw;
      return raw;
    }
    return lastValidRef.current;
  }, [searchParams]);

  const setCurrentTransactionType = useCallback((value: TransactionFormTab | null) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (value) {
        next.set(PARAM_KEY, value);
      } else {
        next.delete(PARAM_KEY);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  useEffect(() => {
    const raw = searchParams.get(PARAM_KEY) as TransactionFormTab | null;
    if (!raw) {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set(PARAM_KEY, lastValidRef.current);
        return next;
      }, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  return [currentTransactionType, setCurrentTransactionType] as const;
};

export default useTransactionFormType;


